import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { PurchasesProvider } from './contexts/PurchasesContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { RecentlyViewedProvider } from './contexts/RecentlyViewedContext';
import { ProductsProvider } from './contexts/ProductsContext';
import { BundlesProvider } from './contexts/BundlesContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { Analytics } from './components/Analytics';
import ShoppingCart from './components/ShoppingCart';
import SkipToMainContent from './components/SkipToMainContent';
import PageLoadingFallback from './components/PageLoadingFallback';
import SiteFilmGrain from './components/SiteFilmGrain';
import ScrollToTopOnRouteChange from './components/ScrollToTopOnRouteChange';
import { Helmet } from 'react-helmet-async';
import { getDefaultOgImageUrl } from './seo/siteMeta';

const HomePage = lazy(() => import('./pages/HomePage'));
const SamplePacksPage = lazy(() => import('./pages/SamplePacksPage'));
const PresetsPage = lazy(() => import('./pages/PresetsPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const BestSellersPage = lazy(() => import('./pages/BestSellersPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LicensingPage = lazy(() => import('./pages/LicensingPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const AccountEditPage = lazy(() => import('./pages/AccountEditPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const BundlesPage = lazy(() => import('./pages/BundlesPage'));
const BundleDetailPage = lazy(() => import('./pages/BundleDetailPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const EmailConfirmedPage = lazy(() => import('./pages/EmailConfirmedPage'));

function App() {
  return (
    <AuthProvider>
      <PurchasesProvider>
      <ProductsProvider>
        <BundlesProvider>
          <WishlistProvider>
          <RecentlyViewedProvider>
          <CartProvider>
          <BrowserRouter>
          <ScrollToTopOnRouteChange />
          <div className="min-h-screen bg-black flex flex-col relative">
            <Helmet>
              <meta property="og:image" content={getDefaultOgImageUrl()} />
              <meta property="og:image:width" content="1200" />
              <meta property="og:image:height" content="630" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:image" content={getDefaultOgImageUrl()} />
            </Helmet>
            <SiteFilmGrain />
            <Analytics />
            <SkipToMainContent />
            <Navbar />
            <ShoppingCart />
            <main id="main-content" className="flex-1 outline-none relative z-10" tabIndex={-1}>
              <Suspense fallback={<PageLoadingFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/samples" element={<SamplePacksPage />} />
                  <Route path="/presets" element={<PresetsPage />} />
                  <Route path="/templates" element={<TemplatesPage />} />
                  <Route path="/best-sellers" element={<BestSellersPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/licensing" element={<LicensingPage />} />
                  <Route path="/refund-policy" element={<RefundPolicyPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/account/edit" element={<AccountEditPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                  <Route path="/bundles" element={<BundlesPage />} />
                  <Route path="/bundles/:slug" element={<BundleDetailPage />} />
                  <Route path="/samples/:slug" element={<ProductDetailPage />} />
                  <Route path="/presets/:slug" element={<ProductDetailPage />} />
                  <Route path="/products/:slug" element={<ProductDetailPage />} />
                  <Route path="/templates/:slug" element={<ProductDetailPage />} />
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        </BrowserRouter>
        </CartProvider>
        </RecentlyViewedProvider>
        </WishlistProvider>
        </BundlesProvider>
      </ProductsProvider>
      </PurchasesProvider>
    </AuthProvider>
  );
}

export default App;
